import { Dropdown, Section, Textfield } from '../formFields'

/** Headline status per stage (retail + retail-food). */
const RETAIL_JOURNEY_ORDER_STATUSES = [
  'Order placed',
  'On the way to store',
  'Order arrived at store',
  'Ready for pickup',
  'Picked up',
]

/** `statusDetail` copy per stage (Luma-style BOPIS). */
const RETAIL_JOURNEY_STATUS_DETAIL_LINES = [
  'We have your order',
  'Your items are heading to the store',
  'Waiting at the pickup location',
  'Head to the pickup desk when you arrive',
  'Thanks — enjoy your purchase',
]

const RETAIL_JOURNEY_EVENT_TYPES = [
  'order.placed',
  'order.onTheWay',
  'order.arrivedAtStore',
  'order.readyForPickup',
  'order.pickedUp',
]

const buildRetailJourneySteps = () =>
  RETAIL_JOURNEY_ORDER_STATUSES.map((orderStatus, i) => ({
    description: orderStatus,
    eventType: RETAIL_JOURNEY_EVENT_TYPES[i],
    step: orderStatus,
    contentState: {
      orderStatus,
      statusDetail: RETAIL_JOURNEY_STATUS_DETAIL_LINES[i],
      currentStage: i,
    },
  }))

/**
 * Live Activities verticals — add a new key here with label, attributes, contentStateFields, and steps.
 */
export const ACTION_FLOW_VERTICALS = {
  'retail-food': {
    label: 'Retail food',
    attributes: {
      storeName: '',
    },
    contentStateFields: [
      { key: 'orderStatus', label: 'Order status' },
      { key: 'statusDetail', label: 'Status detail' },
      { key: 'currentStage', label: 'Current stage', type: 'number', min: 0, max: 4 },
    ],
    steps: buildRetailJourneySteps(),
  },
  retail: {
    label: 'Retail',
    attributes: {
      storeName: '',
    },
    contentStateFields: [
      { key: 'orderStatus', label: 'Order status' },
      { key: 'statusDetail', label: 'Extra message' },
      { key: 'currentStage', label: 'Current stage', type: 'number', min: 0, max: 4 },
    ],
    steps: buildRetailJourneySteps(),
  },
}

/** Default Live Activities vertical when none is set or value is unknown. */
export const ACTION_FLOW_DEFAULT_VERTICAL = 'retail'

export const ACTION_FLOW_VERTICAL_OPTIONS = Object.entries(ACTION_FLOW_VERTICALS).map(([value, config]) => ({
  label: config.label,
  value,
}))

/** Vertical picker; `defaultValue` is `ACTION_FLOW_DEFAULT_VERTICAL`. */
export const actionFlowVerticalField = Dropdown({
  name: 'vertical',
  label: 'Vertical',
  options: ACTION_FLOW_VERTICAL_OPTIONS,
  filter: null,
  defaultValue: ACTION_FLOW_DEFAULT_VERTICAL,
})

export const getActionFlowVerticalConfig = (vertical) => {
  const id = vertical != null && String(vertical).trim() !== '' ? vertical : ACTION_FLOW_DEFAULT_VERTICAL
  return ACTION_FLOW_VERTICALS[id] ?? ACTION_FLOW_VERTICALS[ACTION_FLOW_DEFAULT_VERTICAL]
}

/** Static `content.attributes.*` — extend when a vertical adds more attribute keys. */
export const actionFlowAttributesSection = Section(
  'Attributes',
  Textfield({
    name: 'attributes.storeName',
    label: 'Store name',
    hidden: (cmp) => !('storeName' in (getActionFlowVerticalConfig(cmp.content.vertical).attributes ?? {})),
  })
)

export const actionFlowBaseStepColumns = [
  { key: 'eventType', label: 'Event type' },
  { key: 'step', label: 'Name' },
  { key: 'description', label: 'Description' },
]

export const actionFlowContentStateColumns = (vertical) =>
  (getActionFlowVerticalConfig(vertical).contentStateFields ?? []).map((field) => ({
    key: `contentState.${field.key}`,
    label: field.label,
    ...(field.type && { type: field.type }),
    ...(field.min != null && { min: field.min }),
    ...(field.max != null && { max: field.max }),
    ...(field.step != null && { step: field.step }),
    ...(field.options?.length && { options: field.options }),
  }))

export const getActionFlowStepsColumns = (cmp) => [
  ...actionFlowBaseStepColumns,
  ...actionFlowContentStateColumns(cmp.content.vertical),
]

/** Steps + attributes for a vertical (vertical change + new-component defaults). */
export const getActionFlowVerticalPreset = (vertical, previousAttributes = {}) => {
  const { attributes, steps } = getActionFlowVerticalConfig(vertical)
  return {
    steps: JSON.parse(JSON.stringify(steps ?? [])),
    attributes: {
      ...attributes,
      ...previousAttributes,
    },
  }
}
